import {
  getPrototypeOf,
  isArray,
  isPrivateKey,
  isTerminateValue,
  keys,
  mapPrototype,
  objectPrototype,
  setPrototype
} from '@rehaje/shared'

import { RAW } from './flags'

import type { TArray, TRecord, TWeakMap } from '@rehaje/shared'

export interface ToJS {
  <T = any>(value?: T): T
  seen?: TWeakMap | undefined
  traverse: <T = any>(value?: T, visitor?: Visitor<T>) => T
  visitor?: undefined | Visitor
}

type Visitor<T = any> = (value: T, toJS: (value: T) => T) => T

function internalToJS(value: any): any {
  if (isTerminateValue(value) || value?.__rr_flags & RAW) return value
  if (toJS.seen?.has(value)) return toJS.seen.get(value)
  if (typeof value.toJS === 'function') {
    toJS.seen?.set(value, undefined)
    const result = value.toJS()
    toJS.seen?.set(value, result)
    return result
  }
  if (typeof value.toJSON === 'function') {
    toJS.seen?.set(value, undefined)
    const result = value.toJSON()
    toJS.seen?.set(value, result)
    return result
  }
  if (isArray(value)) {
    const array: TArray = []
    toJS.seen?.set(value, array)
    for (const val of value) array.push(internalToJS(val))
    return array
  }
  let prototype
  if ((prototype = getPrototypeOf(value)) === objectPrototype) {
    const object: TRecord = {}
    toJS.seen?.set(value, object)
    for (const key of keys(value)) {
      if (isPrivateKey(key)) continue
      object[key] = internalToJS(value[key])
    }
    return object
  }
  if (prototype === mapPrototype) {
    const map = new Map()
    toJS.seen?.set(value, map)
    value.forEach((value: any, key: any) => {
      map.set(key, internalToJS(value))
    })
    return map
  }
  if (prototype === setPrototype) {
    const set = new Set()
    toJS.seen?.set(value, set)
    value.forEach((value: any) => {
      set.add(internalToJS(value))
    })
    return set
  }
  return value
}

export const toJS: ToJS = (value) => {
  if (isTerminateValue(value)) return value

  const prevSeen = toJS.seen
  toJS.seen = prevSeen ?? new WeakMap()
  try {
    if (toJS.visitor === undefined) return internalToJS(value)
    return toJS.visitor(value, (value) => internalToJS(value))
  } finally {
    toJS.seen = prevSeen
  }
}

toJS.traverse = (value, visitor) => {
  const prevVisitor = toJS.visitor
  toJS.visitor = visitor
  try {
    return toJS(value)
  } finally {
    toJS.visitor = prevVisitor
  }
}
